import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
    Users, 
    Search,
    MapPin,
    Briefcase,
    MessageSquare,
    User
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MemberDirectoryPage = () => {
    const navigate = useNavigate();
    const { member } = useAuth();
    const [members, setMembers] = useState([]);
    const [filteredMembers, setFilteredMembers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchMembers();
    }, []);

    useEffect(() => {
        filterMembers();
    }, [searchQuery, members]);

    const fetchMembers = async () => {
        try {
            const response = await axios.get(`${API}/member/directory`);
            setMembers(response.data);
            setFilteredMembers(response.data);
        } catch (error) {
            console.error('Error fetching members:', error);
            toast.error('Failed to load directory');
        } finally {
            setLoading(false);
        }
    };

    const filterMembers = () => {
        if (!searchQuery.trim()) {
            setFilteredMembers(members);
            return;
        }
        
        const query = searchQuery.toLowerCase();
        const filtered = members.filter(m => 
            m.name?.toLowerCase().includes(query) ||
            m.username?.toLowerCase().includes(query) ||
            m.profession?.toLowerCase().includes(query) ||
            m.location?.toLowerCase().includes(query) ||
            m.interests?.some(i => i.toLowerCase().includes(query)) ||
            m.hobbies?.some(h => h.toLowerCase().includes(query))
        );
        setFilteredMembers(filtered);
    };

    const startConversation = async (targetMemberId) => {
        try {
            const response = await axios.post(`${API}/member/conversations`, {
                participant_ids: [targetMemberId],
                is_group: false
            });
            navigate(`/member/messages?conversation=${response.data.id}`);
        } catch (error) {
            console.error('Error starting conversation:', error);
            toast.error('Failed to start conversation');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-[#A0A0A0] animate-pulse">Loading directory...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="member-directory">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white font-['Playfair_Display'] flex items-center gap-3">
                        <Users className="w-8 h-8 text-[#C9A227]" />
                        Member Directory
                    </h1>
                    <p className="text-[#A0A0A0] mt-1">
                        Connect with fellow brothers in the community
                    </p>
                </div>
            </div>

            {/* Search */}
            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666]" />
                <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name, profession, location, or interests..."
                    className="pl-10 bg-[#4A5240] border-[#5A6350] text-white placeholder:text-[#666] focus:ring-[#C9A227]/30 focus:border-[#C9A227]"
                    data-testid="directory-search-input"
                />
            </div>

            {/* Info Banner */}
            {members.length === 0 && (
                <Card className="bg-[#4A5240] border-[#5A6350]">
                    <CardContent className="p-6 text-center">
                        <Users className="w-12 h-12 text-[#C9A227] mx-auto mb-4" />
                        <h3 className="text-white font-semibold mb-2">No Members Visible Yet</h3>
                        <p className="text-[#A0A0A0] text-sm">
                            Members appear here when they opt-in to be visible in the directory.
                            <br />
                            Update your privacy settings to appear in the directory and connect with others.
                        </p>
                        <Button 
                            className="mt-4 bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435]"
                            onClick={() => navigate('/member/settings')}
                        >
                            Update Privacy Settings
                        </Button>
                    </CardContent>
                </Card>
            )}

            {/* Results Count */}
            {members.length > 0 && (
                <p className="text-[#A0A0A0] text-sm">
                    Showing {filteredMembers.length} of {members.length} members
                </p>
            )}

            {/* Members Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredMembers.map((m) => (
                    <Card 
                        key={m.id} 
                        className="bg-[#4A5240] border-[#5A6350] hover:border-[#C9A227]/50 transition-all"
                        data-testid={`directory-member-${m.id}`}
                    >
                        <CardContent className="p-5">
                            <div className="flex items-start gap-4">
                                {m.profile_image ? (
                                    <img 
                                        src={m.profile_image.startsWith('/api/uploads') 
                                            ? `${process.env.REACT_APP_BACKEND_URL}${m.profile_image}`
                                            : m.profile_image.startsWith('/uploads')
                                                ? `${process.env.REACT_APP_BACKEND_URL}/api${m.profile_image}` 
                                                : m.profile_image
                                        } 
                                        alt={m.name}
                                        className="w-14 h-14 rounded-xl object-cover flex-shrink-0 bg-[#3D4435]"
                                        onError={(e) => {
                                            // Hide broken image and show initials fallback
                                            e.target.style.display = 'none';
                                            e.target.nextElementSibling.style.display = 'flex';
                                        }}
                                    />
                                ) : null}
                                <div 
                                    className={`w-14 h-14 rounded-xl bg-[#C9A227] items-center justify-center text-[#3D4435] text-xl font-bold flex-shrink-0 ${m.profile_image ? 'hidden' : 'flex'}`}
                                >
                                    {m.name?.charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-white font-semibold truncate">{m.name}</h3>
                                    <p className="text-[#A0A0A0] text-sm">@{m.username}</p>
                                </div>
                            </div>

                            {/* Bio */}
                            {m.bio && (
                                <p className="mt-3 text-sm text-[#A0A0A0] line-clamp-2">{m.bio}</p>
                            )}

                            <div className="mt-4 space-y-2">
                                {m.profession && (
                                    <div className="flex items-center gap-2 text-sm text-[#A0A0A0]">
                                        <Briefcase className="w-4 h-4 text-[#666]" />
                                        <span className="truncate">{m.profession}</span>
                                    </div>
                                )}
                                {m.location && (
                                    <div className="flex items-center gap-2 text-sm text-[#A0A0A0]">
                                        <MapPin className="w-4 h-4 text-[#666]" />
                                        <span className="truncate">{m.location}</span>
                                    </div>
                                )}
                            </div>

                            {/* Interests/Hobbies Tags */}
                            {(m.interests?.length > 0 || m.hobbies?.length > 0) && (
                                <div className="mt-3 flex flex-wrap gap-1">
                                    {[...new Set([...(m.interests || []), ...(m.hobbies || [])])].slice(0, 3).map((tag, idx) => (
                                        <span 
                                            key={idx}
                                            className="px-2 py-0.5 bg-[#3D4435] rounded text-xs text-[#A0A0A0]"
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                    {([...(m.interests || []), ...(m.hobbies || [])].length > 3) && (
                                        <span className="px-2 py-0.5 text-xs text-[#666]">
                                            +{[...(m.interests || []), ...(m.hobbies || [])].length - 3} more
                                        </span>
                                    )}
                                </div>
                            )}

                            {/* Actions */}
                            {m.id !== member?.id && (
                                <div className="mt-4 pt-4 border-t border-[#5A6350] flex gap-2">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="flex-1 border-[#5A6350] text-[#A0A0A0] hover:bg-[#5A6350] hover:text-white"
                                        onClick={() => navigate(`/member/directory/${m.id}`)}
                                        data-testid={`view-profile-${m.id}`}
                                    >
                                        <User className="w-4 h-4 mr-1" />
                                        Profile
                                    </Button>
                                    <Button
                                        size="sm"
                                        className="flex-1 bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435]"
                                        onClick={() => startConversation(m.id)}
                                        data-testid={`message-${m.id}`}
                                    >
                                        <MessageSquare className="w-4 h-4 mr-1" />
                                        Message
                                    </Button>
                                </div>
                            )}

                            {m.id === member?.id && (
                                <div className="mt-4 pt-4 border-t border-[#5A6350]">
                                    <p className="text-center text-[#C9A227] text-sm font-medium">This is you</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* No Results */}
            {filteredMembers.length === 0 && members.length > 0 && (
                <div className="text-center py-12">
                    <Search className="w-12 h-12 text-[#666] mx-auto mb-4" />
                    <h3 className="text-white font-semibold mb-2">No members found</h3>
                    <p className="text-[#A0A0A0] text-sm">
                        Try adjusting your search terms
                    </p>
                </div>
            )}
        </div>
    );
};

export default MemberDirectoryPage;
